import { DisplayableQuad as DisplayableQuadNew } from '@microblink/blinkid-in-browser-sdk';
import {
  BlinkIdSingleSideRecognizerSettings,
  CapturedFrame,
  captureFrame,
  Country,
  createBlinkIdSingleSideRecognizer,
  createIdBarcodeRecognizer,
  createRecognizerRunner,
  DetectionStatus,
  DisplayableQuad,
  DocumentType,
  ErrorMessages,
  ImageOrientation,
  isBrowserSupported,
  loadWasmModule,
  MetadataCallbacks,
  Recognizer,
  RecognizerResult,
  RecognizerResultState,
  RecognizerRunner,
  Region,
  WasmSDK,
  WasmSDKLoadSettings,
  WasmType,
} from 'microblink-old';

import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import { fetchModelSrc } from 'lib/assets';
import EventsClient from 'lib/clients/EventsClient';
import { FaceApiClient } from 'lib/clients/FaceApiClient';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import MicroblinkError, { EventFatalError } from 'lib/errors/MicroblinkError';
import { GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import { captureException, captureMessage } from 'lib/reporting';
import { isInCypress } from 'lib/utils/dom';
import withRetries from 'lib/utils/withRetries';
import { API_ROOT_VERIFY } from 'verify/lib/clients/SessionClientVerify';

interface MicroblinkClientConfig {
  requireFullBack?: boolean;
  allowIncompleteBarcode?: boolean;
  fallbackToFaceApi?: boolean;
}

export interface MicroblinkClientResult {
  recognitionResult: RecognizerResult | null;
  detectedQuad: DisplayableQuadNew | null;
  hasGlare: boolean | null;
  hasIncompleteBack: boolean | null;
  recognizerName: string | undefined;
}

class Client {
  /**
   * On first call to preload, store Promise to load SDK so future preload
   * calls can just wait on Promise if not complete.
   */
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private loadSDKPromise: Promise<WasmSDK>;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private wasmSDK: WasmSDK;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private model: 'basic' | 'advanced';
  /**
   * We've seen Microblink take a very long time to load on mobile networks.
   *
   * At 2.5 seconds in, we start preloading FaceApi as a fallback. We don't
   * do this automatically to avoid bandwidth contention.
   *
   * At 5.0 seconds in, we check Microblink load progress. If it is less
   * than 50%, we switch to GovernmentIdScanner + FaceApi.
   *
   * We don't persist the fallback between page loads. Ideally, Microblink
   * finishes loading in the background and is ready for subsequent attempts.
   *
   * Approximate loading metrics for advanced SDK:
   * (Basic SDK is about half the size)
   *          US  Non-US
   * p80    4.5s     10s
   * p90      9s     18s
   * p95     15s     26s
   * p99     35s     43s
   */
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private faceApiFallbackTimeout: NodeJS.Timeout;
  /**
   * Load percentage of BlinkIDWasmSDK, which is very large (5MB for basic,
   * 10MB for advanced). Expressed as a float between 0 and 100, not a decimal.
   */
  private loadPercentage = 0;
  /**
   * Either a single side recognizer for ID front, or a barcode recognizer.
   */
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private recognizer: Recognizer;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private recognizerRunner: RecognizerRunner;
  /**
   * Separately track initialization status to avoid multiple runner errors
   * when this method is called multiple times in quick succession. If
   * the second call occurs after the first call's teardown but  before the
   * first call's promise resolves, we get an error.
   */
  private initializingRunner = false;
  /**
   * Only used when requireFullBack is true. Acts as a four-corner check.
   *
   * NOTE: we could potentially always detect both quads even if
   * this.requireFullBack is false, and use the full doc quad for tooFar and
   * tooClose checks. We currently use the barcode quad, which is flaky because
   * the barcode quad is small / hard to detect.
   */
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private fullDocumentRecognizer: Recognizer;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private captureSide: GovernmentIdSide | null;
  private idClass: GovernmentIdClass | null;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private currentQuad: DisplayableQuad | null;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private currentFullDocumentQuad: DisplayableQuad | null;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private hasGlare: boolean | null;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private attemptedReset: boolean;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private onClassifiedCallback: (supported: boolean) => void | null;
  /**
   * Enforce BOTH barcode and four corner detection when required side is
   * 'back'.
   */
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private requireFullBack: boolean;
  /**
   * Allow autocapture if a barcode is detected but not necessarily
   * extractable from when required side is 'back'.
   */
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private allowIncompleteBarcode: boolean;
  /**
   * Keep track of the current instance number to prevent race conditions
   * between tear down and initialize.
   * This happens when pressing back in govid redesign, since we go from
   * one capture view to another capture view without anything in between.
   */
  private currentInstanceNumber = 0;

  /**
   * Download and load Microblink SDK only once.
   *
   * The advanced model has optimizations for barcode capture on devices with
   * worse cameras.
   *
   * @param model whether to use the advanced autocapture model.
   */
  public async preload(
    model: 'basic' | 'advanced' = 'advanced',
    wasmType: WasmType | null = null,
  ): Promise<void> {
    if (isInCypress() || import.meta.env.MODE === 'test') {
      return;
    }

    if (this.isLoaded()) {
      return;
    }

    this.model = model;

    if (this.loadSDKPromise == null) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const loadSettings = new WasmSDKLoadSettings(getMicroblinkApiKey(window.location.host));
      loadSettings.engineLocation =
        model === 'advanced'
          ? fetchModelSrc('microblink-6-0-1')
          : fetchModelSrc('microblink-6-0-1-lightweight');
      loadSettings.workerLocation =
        model === 'advanced'
          ? `${API_ROOT_VERIFY}/microblink/worker-script-6-0-1`
          : `${API_ROOT_VERIFY}/microblink/worker-script-6-0-1-lightweight`;
      loadSettings.loadProgressCallback = (pct) => (this.loadPercentage = pct);
      // Only print microblink version info in dev
      loadSettings.allowHelloMessage = import.meta.env.DEV;
      loadSettings.wasmType = wasmType;

      this.loadSDKPromise = withRetries(async () =>
        loadWasmModule(loadSettings)
          .then((wasmSDK) => {
            this.wasmSDK = wasmSDK;
            return this.wasmSDK;
          })
          .catch((err: string | EventFatalError) => {
            // clear promise so preload can be reattempted
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            this.loadSDKPromise = null;
            const microblinkError =
              typeof err === 'string' ? MicroblinkError.fromString(err) : new MicroblinkError(err);
            throw microblinkError;
          }),
      );
    }

    await this.loadSDKPromise;
  }

  public isLoaded(): boolean {
    return this.wasmSDK != null;
  }

  public async initialize(
    captureSide: GovernmentIdSide,
    idClass: GovernmentIdClass = undefined,
    config: MicroblinkClientConfig = {},
  ): Promise<number> {
    const {
      requireFullBack = false,
      allowIncompleteBarcode = false,
      fallbackToFaceApi = false,
    } = config;

    if (isInCypress() || import.meta.env.MODE === 'test') {
      return -1;
    }

    this.currentQuad = null;
    this.hasGlare = null;

    this.attemptedReset = false;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.onClassifiedCallback = null;

    if (
      this.recognizer &&
      this.recognizerRunner &&
      this.captureSide === captureSide &&
      this.idClass === idClass
    ) {
      // can use existing runner since capture side and id class have
      // not changed since last initialization
      return this.currentInstanceNumber;
    }

    // increment instance number to lock out tear down calls of previous instance
    // which can conflict with the startRunner call (which will already tear
    // down any existing instances anyways)
    this.currentInstanceNumber++;

    this.captureSide = captureSide;
    this.idClass = idClass;
    this.requireFullBack = captureSide === GovernmentIdSide.Back && requireFullBack;
    this.allowIncompleteBarcode = captureSide === GovernmentIdSide.Back && allowIncompleteBarcode;

    return new Promise((res, rej) => {
      if (fallbackToFaceApi) {
        this.faceApiFallbackTimeout = setTimeout(() => {
          FaceApiClient.loadModels();

          this.faceApiFallbackTimeout = setTimeout(() => {
            if (this.loadPercentage < 50) {
              EventsClient.logAmplitude('government-id.microblink.load-timeout', {
                model: this.model,
              });
              rej(
                MicroblinkError.fromString(
                  '[IGNORED] Microblink initialization took longer than 5000ms, likely due to a slow network connection. Falling back to FaceApi.',
                ),
              );
            }
          }, 2500);
        }, 2500);
      }

      this.preload()
        .then(() => clearTimeout(this.faceApiFallbackTimeout))
        .then(() => this.startRunner())
        .then(() => res(this.currentInstanceNumber))
        .catch((err) =>
          typeof err === 'string' ? MicroblinkError.fromString(err) : new MicroblinkError(err),
        ); // needed to properly raise error in caller
    });
  }

  /**
   * WARNING: This method instantiates this.recognizer, does some async logic,
   * then modifies this.recognizer. It is possible that the client is destroyed
   * in the middle of async logic. Thus, we must always check for existence of
   * this.recognizer after every await.
   */
  private async startRunner() {
    if (!this.captureSide) {
      return;
    }

    if (this.initializingRunner) {
      return;
    }

    this.initializingRunner = true;

    const initializationTime = performance.now();

    // Clean up any existing runners that weren't deleted
    await this.tearDown(false);

    // if a different tear down process has cleared the capture side
    // during the previous async call do not continue
    if (!this.captureSide) {
      return;
    }
    if (this.captureSide === GovernmentIdSide.Back && this.allowIncompleteBarcode) {
      /**
       * Currently gated by inquiry:enable-microblink-incomplete-barcode-detection-desktop.
       * Uses the single side recognizer instead of the barcode recognizer,
       * resulting in a less strict recognizer. Requires EITHER:
       * 1. Full barcode detected/extracted
       * 2. 4 corners detected + barcode detected but not extracted
       */
      this.recognizer = await createBlinkIdSingleSideRecognizer(this.wasmSDK);
      const recognizerSettings =
        (await this.recognizer.currentSettings()) as BlinkIdSingleSideRecognizerSettings;
      // enables detection of barcode
      recognizerSettings.recognitionModeFilter.enableBarcodeId = true;
      recognizerSettings.recognitionModeFilter.enableMrzPassport = false;
      recognizerSettings.recognitionModeFilter.enableMrzVisa = false;
      recognizerSettings.recognitionModeFilter.enableMrzId = false;
      recognizerSettings.recognitionModeFilter.enablePhotoId = false;
      // enables detection of entire ID's quad
      recognizerSettings.recognitionModeFilter.enableFullDocumentRecognition = true;
      await this.recognizer?.updateSettings(recognizerSettings);
    } else if (
      this.captureSide === GovernmentIdSide.BarcodePdf417 ||
      this.captureSide === GovernmentIdSide.Back
    ) {
      /**
       * Enforces detection AND extraction of barcode.
       */
      this.recognizer = await createIdBarcodeRecognizer(this.wasmSDK);
    } else if (this.captureSide === GovernmentIdSide.Front) {
      this.recognizer = await createBlinkIdSingleSideRecognizer(this.wasmSDK);
      const recognizerSettings =
        (await this.recognizer.currentSettings()) as BlinkIdSingleSideRecognizerSettings;
      recognizerSettings.recognitionModeFilter.enableBarcodeId = false;
      recognizerSettings.recognitionModeFilter.enableMrzPassport = this.idClass === 'pp';
      recognizerSettings.recognitionModeFilter.enableMrzVisa = false;
      recognizerSettings.recognitionModeFilter.enableMrzId = false;
      recognizerSettings.classifierCallback = (supported: boolean) =>
        this.onClassifiedCallback?.(supported);
      await this.recognizer?.updateSettings(recognizerSettings);
    } else {
      captureMessage(`Unsupported side for Microblink Client: ${this.captureSide}`, 'warning');
    }

    if (this.requireFullBack) {
      /**
       * Currently gated by inquiry:enable-microblink-full-back-detection-desktop/mobile.
       * Adds an EXTRA recognizer that enforces 4 corners check, resulting in a stricter combined recognizer.
       * resulting in a less strict recognizer. Requires BOTH:
       * 1. Full barcode detected/extracted
       * 2. 4 corners detected
       */
      this.fullDocumentRecognizer = await createBlinkIdSingleSideRecognizer(this.wasmSDK);
      const recognizerSettings =
        (await this.fullDocumentRecognizer.currentSettings()) as BlinkIdSingleSideRecognizerSettings;
      recognizerSettings.recognitionModeFilter.enableBarcodeId = false;
      recognizerSettings.recognitionModeFilter.enableMrzPassport = false;
      recognizerSettings.recognitionModeFilter.enableMrzVisa = false;
      recognizerSettings.recognitionModeFilter.enableMrzId = false;
      recognizerSettings.recognitionModeFilter.enablePhotoId = false;
      recognizerSettings.recognitionModeFilter.enableFullDocumentRecognition = true;
      await this.fullDocumentRecognizer?.updateSettings(recognizerSettings);
    }

    if (this.requireFullBack || this.allowIncompleteBarcode) {
      const metadataCallbacks: MetadataCallbacks = {
        onQuadDetection: (quad: DisplayableQuad) => {
          // This can be called up to 2 times, once with the barcode quad, and once with the quad for the full ID.
          // Generally speaking, the barcode recognizer calls this callback first, but that isn't garunteed,
          // If this is called twice, We take the smaller detected quad and consider that the barcode quad (this.currentQuad) and the larger one as the
          // quad (this.currentFullDocumentQuad) for the full ID.
          // If this is only called once, the quad will be assigned to this.currentQuad, but we do a check later to guess whether the quad is a barcode or a full ID.
          if (this.currentQuad) {
            if (getWidth(quad) > getWidth(this.currentQuad)) {
              // newly detected quad is larger, store it as the full document quad
              this.currentFullDocumentQuad = quad;
            } else {
              // the first detected quad is larger, store the new quad as the barcode quad, and set the full document quad to the first detected quad
              this.currentFullDocumentQuad = this.currentQuad;
              this.currentQuad = quad;
            }
          } else {
            this.currentQuad = quad;
          }
        },
        onGlare: (hasGlare: boolean) => {
          this.hasGlare = hasGlare;
        },
      };

      this.recognizerRunner = await createRecognizerRunner(
        this.wasmSDK,
        this.requireFullBack ? [this.recognizer, this.fullDocumentRecognizer] : [this.recognizer],
        true, // this forces both recognizers to run regardless the outcome of the first
        metadataCallbacks,
      );
    } else {
      const metadataCallbacks: MetadataCallbacks = {
        onQuadDetection: (quad: DisplayableQuad) => {
          // if capture side is barcode or back (uses barcode recognizer),
          // only accept the first quad detected
          // When Barcode recognizer fails to detect a barcode, it can trigger
          // this callback multiple times, feeding a spurious quad on the last call.
          // Discussion with microblink is here https://withpersona-dev.slack.com/archives/C02879VL0CS/p1677150295384929
          if (this.captureSide === GovernmentIdSide.Front || !this.currentQuad) {
            this.currentQuad = quad;
          }
        },
        onGlare: (hasGlare: boolean) => {
          this.hasGlare = hasGlare;
        },
      };

      this.recognizerRunner = await createRecognizerRunner(
        this.wasmSDK,
        [this.recognizer],
        false,
        metadataCallbacks,
      );
    }

    this.initializingRunner = false;

    EventsClient.logAmplitude('government-id.microblink.initialize-runners', {
      durationSeconds: (performance.now() - initializationTime) / 1000,
      side: this.captureSide,
      idClass: this.idClass,
    });
  }

  /**
   * Passes an image to Microblink and runs a simple recognizer on it. This is meant
   * to be used for OCR-ing an unknown ID.
   * @param imageDataUri The data uri of the image to extract text from
   * @returns a RecognizerResult with the details of extracted text
   */
  public async extractTextFromImage(imageDataUri: string): Promise<MicroblinkClientResult | null> {
    if (!this.recognizerRunner || !this.recognizer) {
      return null;
    }
    const image = new Image();
    image.src = imageDataUri;
    await image.decode();
    const imageFrame = captureFrame(image);
    const processResult = await this.recognizerRunner.processImage(imageFrame);

    let recognitionResult = null;
    if (processResult !== RecognizerResultState.Empty) {
      recognitionResult = await this.recognizer.getResult();
    }
    return {
      recognitionResult,
      recognizerName: this.recognizer.recognizerName,
      detectedQuad: null,
      hasGlare: null,
      hasIncompleteBack: null,
    };
  }

  /**
   * Passes a canvas to Microblink and runs recognizers on it.
   * The canvas is expected to have a video frame drawn to it already via
   * bufferImageWithOverlayWindow().
   */
  public async processImage(
    canvasBuffer: HTMLCanvasElement,
    /**
     * Fired when recognizers successfully classify a document.
     */
    onClassifiedCallback: (supported: boolean) => void,
  ): Promise<MicroblinkClientResult> {
    if (!this.recognizerRunner || !this.recognizer) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return null;
    }
    this.currentQuad = null;
    this.currentFullDocumentQuad = null;
    this.hasGlare = null;
    this.onClassifiedCallback = onClassifiedCallback;

    // replicates logic in captureFrame()
    // https://github.com/BlinkID/blinkid-in-browser/blob/fcbb1ab3a5871e3834ff6fbdc96df69e9c72eed0/src/MicroblinkSDK/FrameCapture.ts#L42
    //
    // so we can skip its internal logic of creating a new canvas
    // and copying the frame over
    const ctx = canvasBuffer.getContext('2d', { willReadFrequently: true });
    const capturedFrame = new CapturedFrame(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ctx?.getImageData(0, 0, canvasBuffer.width, canvasBuffer.height),
      ImageOrientation.NoRotation,
      true, // marks frame as being captured from video
    );
    if (!capturedFrame.imageData) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return null;
    }

    const processResult = await this.recognizerRunner
      .processImage(capturedFrame)
      .catch(async (err: string | EventFatalError): Promise<null> => {
        const msg = typeof err === 'string' ? err : err.message;
        if (
          (msg.indexOf(ErrorMessages.WORKER_IMAGE_PROCESS_FAILURE) !== -1 ||
            msg.indexOf(ErrorMessages.WORKER_RUNNER_DELETED) !== -1) &&
          !this.attemptedReset
        ) {
          this.attemptedReset = true;
          await this.tearDown(false);
          await this.startRunner();
          return null;
        } else {
          const microblinkError =
            typeof err === 'string' ? MicroblinkError.fromString(err) : new MicroblinkError(err);
          throw microblinkError;
        }
      });
    const recognizerName = this.recognizer?.recognizerName ?? 'MicroblinkUnknown';

    if (this.requireFullBack) {
      const barcodeRecognitionResult = await this.recognizer?.getResult();
      const recognitionResult =
        barcodeRecognitionResult?.state !== RecognizerResultState.Empty
          ? barcodeRecognitionResult
          : null;
      const fullDocumentRecognitionResult = await this.fullDocumentRecognizer?.getResult();

      // if only one quad detected, use this heuristic to check if we should consider the detected quad the
      // 4 corners of the ID instead of the barcode
      if (
        !this.currentFullDocumentQuad &&
        this.currentQuad &&
        barcodeRecognitionResult?.state === RecognizerResultState.Empty &&
        fullDocumentRecognitionResult?.state !== RecognizerResultState.Empty &&
        isValidDLQuad(this.currentQuad, null)
      ) {
        this.currentFullDocumentQuad = this.currentQuad;
        this.currentQuad = null;
      }

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const detectedQuad = isValidDLQuad(this.currentFullDocumentQuad, this.currentQuad)
        ? this.currentFullDocumentQuad
        : null;

      const hasIncompleteBack = !!recognitionResult && !detectedQuad;

      return {
        recognitionResult,
        detectedQuad: detectedQuad as DisplayableQuadNew | null,
        hasGlare: this.hasGlare,
        hasIncompleteBack,
        recognizerName,
      };
    } else {
      const recognitionResult =
        processResult !== RecognizerResultState.Empty ? await this.recognizer?.getResult() : null;

      let detectedQuad = this.currentQuad;

      if (this.allowIncompleteBarcode) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        detectedQuad = isValidDLQuad(this.currentFullDocumentQuad, null)
          ? this.currentFullDocumentQuad
          : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            isValidDLQuad(this.currentQuad, null)
            ? this.currentQuad
            : null;
      }

      return {
        recognitionResult,
        detectedQuad: detectedQuad as DisplayableQuadNew | null,
        hasGlare: this.hasGlare,
        hasIncompleteBack: null,
        recognizerName,
      };
    }
  }

  public needsTeardown() {
    return (
      this.recognizerRunner != null ||
      this.recognizer != null ||
      this.fullDocumentRecognizer != null
    );
  }

  public async tearDownInstance(instanceNumber: number | undefined) {
    if (instanceNumber === this.currentInstanceNumber) {
      this.tearDown();
    }
  }

  public async tearDown(clearCaptureSide = true) {
    // unset all variables before doing async deletion calls,
    // to prevent a separate initialization call from being overriden.
    const recognizerRunnerToDelete = this.recognizerRunner;
    const recognizerToDelete = this.recognizer;
    const fullDocumentRecognizerToDelete = this.fullDocumentRecognizer;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.recognizer = undefined;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.recognizerRunner = undefined;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.fullDocumentRecognizer = undefined;
    if (clearCaptureSide) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.captureSide = undefined;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.requireFullBack = undefined;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.allowIncompleteBarcode = undefined;
    }
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.currentQuad = undefined;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.currentFullDocumentQuad = undefined;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.hasGlare = undefined;

    try {
      await Promise.allSettled([
        recognizerRunnerToDelete?.delete(),
        recognizerToDelete?.delete(),
        fullDocumentRecognizerToDelete?.delete(),
      ]);
    } catch (e) {
      // Do nothing if the runners are already deleted
      null;
    }
  }
}

const ALL_ID_CARD_SIDES = [
  GovernmentIdSide.Front,
  GovernmentIdSide.Back,
  GovernmentIdSide.BarcodePdf417,
];

const SUPPORTED_PASSPORT_CLASSES = ['pp'];

type MicroblinkDomain =
  | 'withpersona.com'
  | 'withpersona-staging.com'
  | '*.withpersona.com'
  | '*.withpersona-staging.com';

let LICENSES: Record<MicroblinkDomain, { key: string; expiresAt: string }> = {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  'withpersona.com': { key: null, expiresAt: null },
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  'withpersona-staging.com': { key: null, expiresAt: null },
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  '*.withpersona.com': { key: null, expiresAt: null },
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  '*.withpersona-staging.com': { key: null, expiresAt: null },
};
try {
  LICENSES = JSON.parse(import.meta.env.VITE_MICROBLINK_LICENSES);
} catch {
  // Couldn't load licenses -- should only happen in tests
}

const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

export const getMicroblinkApiKey = (host: string): string | null => {
  // Remove any ports from host (mostly for localhost purposes)
  let lookupDomain: MicroblinkDomain = host.split(':')[0] as MicroblinkDomain;

  // Convert anything.withpersona.com to *.withpersona.com
  if (lookupDomain.split('.').length === 3) {
    const domainParts = lookupDomain.split('.');
    domainParts[0] = '*';
    lookupDomain = domainParts.join('.') as MicroblinkDomain;
  }

  const { key, expiresAt } = LICENSES[lookupDomain] ?? {};

  try {
    // Alert if Microblink licenses are about to expire on production.
    if (
      expiresAt != null &&
      (lookupDomain === 'withpersona.com' || lookupDomain === '*.withpersona.com')
    ) {
      const timeToExpiryMs = new Date(expiresAt).getTime() - new Date().getTime();

      if (timeToExpiryMs < TWO_WEEKS_MS) {
        captureMessage(
          'Microblink license key is expiring in less than 14 days. Request a renewal in #microblink-persona and update MICROBLINK_LICENSES in config/webpack/production.js.',
          'error',
          {
            expiresAt,
            domain: lookupDomain,
          },
        );
      }
    }
  } catch (e) {
    captureException(e);
  }

  return key;
};

export const canUseMicroblink = (host: string): boolean => {
  return getMicroblinkApiKey(host) != null && isBrowserSupported();
};

export const isMicroblinkSupportedSide = (
  side: GovernmentIdSide,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  selectedCountryCode: CountryCode = undefined,
  governmentIdClass: GovernmentIdClass,
  featureFlags: FeatureFlagsMap,
): boolean => {
  // Use Microblink for passport front for all countries.
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (SUPPORTED_PASSPORT_CLASSES.includes(governmentIdClass) && side === GovernmentIdSide.Front) {
    return true;
  }

  if (
    featureFlags['inquiry:enable-microblink-web-front-everywhere'] &&
    side === GovernmentIdSide.Front
  ) {
    return true;
  }

  if (
    featureFlags['inquiry:enable-microblink-web-back-everywhere'] &&
    side === GovernmentIdSide.Back
  ) {
    return true;
  }

  // See go/microblink for instructions on adding support for other ID types.
  switch (selectedCountryCode) {
    case 'US':
      switch (governmentIdClass) {
        case 'dl':
        case 'id':
          return ALL_ID_CARD_SIDES.includes(side);
        default:
          return false;
      }
    case 'CA':
      if (!featureFlags['inquiry:enable-microblink-canada']) return false;

      switch (governmentIdClass) {
        case 'dl':
        case 'id':
          return ALL_ID_CARD_SIDES.includes(side);
        default:
          return false;
      }
    case 'JP':
      // As of v6, these + passport are the only supported JP types.
      // We investigated enabling these to improve image quality for extractions, but did not appear to help.
      // Can revisit in the future.
      // switch (governmentIdClass) {
      //   case 'dl':
      //   case 'myn':
      //     return side === GovernmentIdSide.Front;
      //   default:
      //     return false;
      // }
      return false;
    default:
      return false;
  }
};

const countryToStringMap: Record<Country, string> = {
  [Country.NONE]: 'NONE',
  [Country.ALBANIA]: 'ALBANIA',
  [Country.ALGERIA]: 'ALGERIA',
  [Country.ARGENTINA]: 'ARGENTINA',
  [Country.AUSTRALIA]: 'AUSTRALIA',
  [Country.AUSTRIA]: 'AUSTRIA',
  [Country.AZERBAIJAN]: 'AZERBAIJAN',
  [Country.BAHRAIN]: 'BAHRAIN',
  [Country.BANGLADESH]: 'BANGLADESH',
  [Country.BELGIUM]: 'BELGIUM',
  [Country.BOSNIA_AND_HERZEGOVINA]: 'BOSNIA_AND_HERZEGOVINA',
  [Country.BRUNEI]: 'BRUNEI',
  [Country.BULGARIA]: 'BULGARIA',
  [Country.CAMBODIA]: 'CAMBODIA',
  [Country.CANADA]: 'CANADA',
  [Country.CHILE]: 'CHILE',
  [Country.COLOMBIA]: 'COLOMBIA',
  [Country.COSTA_RICA]: 'COSTA_RICA',
  [Country.CROATIA]: 'CROATIA',
  [Country.CYPRUS]: 'CYPRUS',
  [Country.CZECHIA]: 'CZECHIA',
  [Country.DENMARK]: 'DENMARK',
  [Country.DOMINICAN_REPUBLIC]: 'DOMINICAN_REPUBLIC',
  [Country.EGYPT]: 'EGYPT',
  [Country.ESTONIA]: 'ESTONIA',
  [Country.FINLAND]: 'FINLAND',
  [Country.FRANCE]: 'FRANCE',
  [Country.GEORGIA]: 'GEORGIA',
  [Country.GERMANY]: 'GERMANY',
  [Country.GHANA]: 'GHANA',
  [Country.GREECE]: 'GREECE',
  [Country.GUATEMALA]: 'GUATEMALA',
  [Country.HONG_KONG]: 'HONG_KONG',
  [Country.HUNGARY]: 'HUNGARY',
  [Country.INDIA]: 'INDIA',
  [Country.INDONESIA]: 'INDONESIA',
  [Country.IRELAND]: 'IRELAND',
  [Country.ISRAEL]: 'ISRAEL',
  [Country.ITALY]: 'ITALY',
  [Country.JORDAN]: 'JORDAN',
  [Country.KAZAKHSTAN]: 'KAZAKHSTAN',
  [Country.KENYA]: 'KENYA',
  [Country.KOSOVO]: 'KOSOVO',
  [Country.KUWAIT]: 'KUWAIT',
  [Country.LATVIA]: 'LATVIA',
  [Country.LITHUANIA]: 'LITHUANIA',
  [Country.MALAYSIA]: 'MALAYSIA',
  [Country.MALDIVES]: 'MALDIVES',
  [Country.MALTA]: 'MALTA',
  [Country.MAURITIUS]: 'MAURITIUS',
  [Country.MEXICO]: 'MEXICO',
  [Country.MOROCCO]: 'MOROCCO',
  [Country.NETHERLANDS]: 'NETHERLANDS',
  [Country.NEW_ZEALAND]: 'NEW_ZEALAND',
  [Country.NIGERIA]: 'NIGERIA',
  [Country.PAKISTAN]: 'PAKISTAN',
  [Country.PANAMA]: 'PANAMA',
  [Country.PARAGUAY]: 'PARAGUAY',
  [Country.PHILIPPINES]: 'PHILIPPINES',
  [Country.POLAND]: 'POLAND',
  [Country.PORTUGAL]: 'PORTUGAL',
  [Country.PUERTO_RICO]: 'PUERTO_RICO',
  [Country.QATAR]: 'QATAR',
  [Country.ROMANIA]: 'ROMANIA',
  [Country.RUSSIA]: 'RUSSIA',
  [Country.SAUDI_ARABIA]: 'SAUDI_ARABIA',
  [Country.SERBIA]: 'SERBIA',
  [Country.SINGAPORE]: 'SINGAPORE',
  [Country.SLOVAKIA]: 'SLOVAKIA',
  [Country.SLOVENIA]: 'SLOVENIA',
  [Country.SOUTH_AFRICA]: 'SOUTH_AFRICA',
  [Country.SPAIN]: 'SPAIN',
  [Country.SWEDEN]: 'SWEDEN',
  [Country.SWITZERLAND]: 'SWITZERLAND',
  [Country.TAIWAN]: 'TAIWAN',
  [Country.THAILAND]: 'THAILAND',
  [Country.TUNISIA]: 'TUNISIA',
  [Country.TURKEY]: 'TURKEY',
  [Country.UAE]: 'UAE',
  [Country.UGANDA]: 'UGANDA',
  [Country.UK]: 'UK',
  [Country.UKRAINE]: 'UKRAINE',
  [Country.USA]: 'USA',
  [Country.VIETNAM]: 'VIETNAM',
  [Country.BRAZIL]: 'BRAZIL',
  [Country.NORWAY]: 'NORWAY',
  [Country.OMAN]: 'OMAN',
  [Country.ECUADOR]: 'ECUADOR',
  [Country.EL_SALVADOR]: 'EL_SALVADOR',
  [Country.SRI_LANKA]: 'SRI_LANKA',
  [Country.PERU]: 'PERU',
  [Country.URUGUAY]: 'URUGUAY',
  [Country.BAHAMAS]: 'BAHAMAS',
  [Country.BERMUDA]: 'BERMUDA',
  [Country.BOLIVIA]: 'BOLIVIA',
  [Country.CHINA]: 'CHINA',
  [Country.EUROPEAN_UNION]: 'EUROPEAN_UNION',
  [Country.HAITI]: 'HAITI',
  [Country.HONDURAS]: 'HONDURAS',
  [Country.ICELAND]: 'ICELAND',
  [Country.JAPAN]: 'JAPAN',
  [Country.LUXEMBOURG]: 'LUXEMBOURG',
  [Country.MONTENEGRO]: 'MONTENEGRO',
  [Country.NICARAGUA]: 'NICARAGUA',
  [Country.SOUTH_KOREA]: 'SOUTH_KOREA',
  [Country.VENEZUELA]: 'VENEZUELA',
  [Country.AFGHANISTAN]: 'AFGHANISTAN',
  [Country.ALAND_ISLANDS]: 'ALAND_ISLANDS',
  [Country.AMERICAN_SAMOA]: 'AMERICAN_SAMOA',
  [Country.ANDORRA]: 'ANDORRA',
  [Country.ANGOLA]: 'ANGOLA',
  [Country.ANGUILLA]: 'ANGUILLA',
  [Country.ANTARCTICA]: 'ANTARCTICA',
  [Country.ANTIGUA_AND_BARBUDA]: 'ANTIGUA_AND_BARBUDA',
  [Country.ARMENIA]: 'ARMENIA',
  [Country.ARUBA]: 'ARUBA',
  [Country.BAILIWICK_OF_GUERNSEY]: 'BAILIWICK_OF_GUERNSEY',
  [Country.BAILIWICK_OF_JERSEY]: 'BAILIWICK_OF_JERSEY',
  [Country.BARBADOS]: 'BARBADOS',
  [Country.BELARUS]: 'BELARUS',
  [Country.BELIZE]: 'BELIZE',
  [Country.BENIN]: 'BENIN',
  [Country.BHUTAN]: 'BHUTAN',
  [Country.BONAIRE_SAINT_EUSTATIUS_AND_SABA]: 'BONAIRE_SAINT_EUSTATIUS_AND_SABA',
  [Country.BOTSWANA]: 'BOTSWANA',
  [Country.BOUVET_ISLAND]: 'BOUVET_ISLAND',
  [Country.BRITISH_INDIAN_OCEAN_TERRITORY]: 'BRITISH_INDIAN_OCEAN_TERRITORY',
  [Country.BURKINA_FASO]: 'BURKINA_FASO',
  [Country.BURUNDI]: 'BURUNDI',
  [Country.CAMEROON]: 'CAMEROON',
  [Country.CAPE_VERDE]: 'CAPE_VERDE',
  [Country.CARIBBEAN_NETHERLANDS]: 'CARIBBEAN_NETHERLANDS',
  [Country.CAYMAN_ISLANDS]: 'CAYMAN_ISLANDS',
  [Country.CENTRAL_AFRICAN_REPUBLIC]: 'CENTRAL_AFRICAN_REPUBLIC',
  [Country.CHAD]: 'CHAD',
  [Country.CHRISTMAS_ISLAND]: 'CHRISTMAS_ISLAND',
  [Country.COCOS_ISLANDS]: 'COCOS_ISLANDS',
  [Country.COMOROS]: 'COMOROS',
  [Country.CONGO]: 'CONGO',
  [Country.COOK_ISLANDS]: 'COOK_ISLANDS',
  [Country.CUBA]: 'CUBA',
  [Country.CURACAO]: 'CURACAO',
  [Country.DEMOCRATIC_REPUBLIC_OF_THE_CONGO]: 'DEMOCRATIC_REPUBLIC_OF_THE_CONGO',
  [Country.DJIBOUTI]: 'DJIBOUTI',
  [Country.DOMINICA]: 'DOMINICA',
  [Country.EAST_TIMOR]: 'EAST_TIMOR',
  [Country.EQUATORIAL_GUINEA]: 'EQUATORIAL_GUINEA',
  [Country.ERITREA]: 'ERITREA',
  [Country.ESWATINI]: 'ESWATINI',
  [Country.ETHIOPIA]: 'ETHIOPIA',
  [Country.FALKLAND_ISLANDS]: 'FALKLAND_ISLANDS',
  [Country.FAROE_ISLANDS]: 'FAROE_ISLANDS',
  [Country.FEDERATED_STATES_OF_MICRONESIA]: 'FEDERATED_STATES_OF_MICRONESIA',
  [Country.FIJI]: 'FIJI',
  [Country.FRENCH_GUIANA]: 'FRENCH_GUIANA',
  [Country.FRENCH_POLYNESIA]: 'FRENCH_POLYNESIA',
  [Country.FRENCH_SOUTHERN_TERRITORIES]: 'FRENCH_SOUTHERN_TERRITORIES',
  [Country.GABON]: 'GABON',
  [Country.GAMBIA]: 'GAMBIA',
  [Country.GIBRALTAR]: 'GIBRALTAR',
  [Country.GREENLAND]: 'GREENLAND',
  [Country.GRENADA]: 'GRENADA',
  [Country.GUADELOUPE]: 'GUADELOUPE',
  [Country.GUAM]: 'GUAM',
  [Country.GUINEA]: 'GUINEA',
  [Country.GUINEA_BISSAU]: 'GUINEA_BISSAU',
  [Country.GUYANA]: 'GUYANA',
  [Country.HEARD_ISLAND_AND_MCDONALD_ISLANDS]: 'HEARD_ISLAND_AND_MCDONALD_ISLANDS',
  [Country.IRAN]: 'IRAN',
  [Country.IRAQ]: 'IRAQ',
  [Country.ISLE_OF_MAN]: 'ISLE_OF_MAN',
  [Country.IVORY_COAST]: 'IVORY_COAST',
  [Country.JAMAICA]: 'JAMAICA',
  [Country.KIRIBATI]: 'KIRIBATI',
  [Country.KYRGYZSTAN]: 'KYRGYZSTAN',
  [Country.LAOS]: 'LAOS',
  [Country.LEBANON]: 'LEBANON',
  [Country.LESOTHO]: 'LESOTHO',
  [Country.LIBERIA]: 'LIBERIA',
  [Country.LIBYA]: 'LIBYA',
  [Country.LIECHTENSTEIN]: 'LIECHTENSTEIN',
  [Country.MACAU]: 'MACAU',
  [Country.MADAGASCAR]: 'MADAGASCAR',
  [Country.MALAWI]: 'MALAWI',
  [Country.MALI]: 'MALI',
  [Country.MARSHALL_ISLANDS]: 'MARSHALL_ISLANDS',
  [Country.MARTINIQUE]: 'MARTINIQUE',
  [Country.MAURITANIA]: 'MAURITANIA',
  [Country.MAYOTTE]: 'MAYOTTE',
  [Country.MOLDOVA]: 'MOLDOVA',
  [Country.MONACO]: 'MONACO',
  [Country.MONGOLIA]: 'MONGOLIA',
  [Country.MONTSERRAT]: 'MONTSERRAT',
  [Country.MOZAMBIQUE]: 'MOZAMBIQUE',
  [Country.MYANMAR]: 'MYANMAR',
  [Country.NAMIBIA]: 'NAMIBIA',
  [Country.NAURU]: 'NAURU',
  [Country.NEPAL]: 'NEPAL',
  [Country.NEW_CALEDONIA]: 'NEW_CALEDONIA',
  [Country.NIGER]: 'NIGER',
  [Country.NIUE]: 'NIUE',
  [Country.NORFOLK_ISLAND]: 'NORFOLK_ISLAND',
  [Country.NORTHERN_CYPRUS]: 'NORTHERN_CYPRUS',
  [Country.NORTHERN_MARIANA_ISLANDS]: 'NORTHERN_MARIANA_ISLANDS',
  [Country.NORTH_KOREA]: 'NORTH_KOREA',
  [Country.NORTH_MACEDONIA]: 'NORTH_MACEDONIA',
  [Country.PALAU]: 'PALAU',
  [Country.PALESTINE]: 'PALESTINE',
  [Country.PAPUA_NEW_GUINEA]: 'PAPUA_NEW_GUINEA',
  [Country.PITCAIRN]: 'PITCAIRN',
  [Country.REUNION]: 'REUNION',
  [Country.RWANDA]: 'RWANDA',
  [Country.SAINT_BARTHELEMY]: 'SAINT_BARTHELEMY',
  [Country.SAINT_HELENA_ASCENSION_AND_TRISTIAN_DA_CUNHA]:
    'SAINT_HELENA_ASCENSION_AND_TRISTIAN_DA_CUNHA',
  [Country.SAINT_KITTS_AND_NEVIS]: 'SAINT_KITTS_AND_NEVIS',
  [Country.SAINT_LUCIA]: 'SAINT_LUCIA',
  [Country.SAINT_MARTIN]: 'SAINT_MARTIN',
  [Country.SAINT_PIERRE_AND_MIQUELON]: 'SAINT_PIERRE_AND_MIQUELON',
  [Country.SAINT_VINCENT_AND_THE_GRENADINES]: 'SAINT_VINCENT_AND_THE_GRENADINES',
  [Country.SAMOA]: 'SAMOA',
  [Country.SAN_MARINO]: 'SAN_MARINO',
  [Country.SAO_TOME_AND_PRINCIPE]: 'SAO_TOME_AND_PRINCIPE',
  [Country.SENEGAL]: 'SENEGAL',
  [Country.SEYCHELLES]: 'SEYCHELLES',
  [Country.SIERRA_LEONE]: 'SIERRA_LEONE',
  [Country.SINT_MAARTEN]: 'SINT_MAARTEN',
  [Country.SOLOMON_ISLANDS]: 'SOLOMON_ISLANDS',
  [Country.SOMALIA]: 'SOMALIA',
  [Country.SOUTH_GEORGIA_AND_THE_SOUTH_SANDWICH_ISLANDS]:
    'SOUTH_GEORGIA_AND_THE_SOUTH_SANDWICH_ISLANDS',
  [Country.SOUTH_SUDAN]: 'SOUTH_SUDAN',
  [Country.SUDAN]: 'SUDAN',
  [Country.SURINAME]: 'SURINAME',
  [Country.SVALBARD_AND_JAN_MAYEN]: 'SVALBARD_AND_JAN_MAYEN',
  [Country.SYRIA]: 'SYRIA',
  [Country.TAJIKISTAN]: 'TAJIKISTAN',
  [Country.TANZANIA]: 'TANZANIA',
  [Country.TOGO]: 'TOGO',
  [Country.TOKELAU]: 'TOKELAU',
  [Country.TONGA]: 'TONGA',
  [Country.TRINIDAD_AND_TOBAGO]: 'TRINIDAD_AND_TOBAGO',
  [Country.TURKMENISTAN]: 'TURKMENISTAN',
  [Country.TURKS_AND_CAICOS_ISLANDS]: 'TURKS_AND_CAICOS_ISLANDS',
  [Country.TUVALU]: 'TUVALU',
  [Country.UNITED_STATES_MINOR_OUTLYING_ISLANDS]: 'UNITED_STATES_MINOR_OUTLYING_ISLANDS',
  [Country.UZBEKISTAN]: 'UZBEKISTAN',
  [Country.VANUATU]: 'VANUATU',
  [Country.VATICAN_CITY]: 'VATICAN_CITY',
  [Country.VIRGIN_ISLANDS_BRITISH]: 'VIRGIN_ISLANDS_BRITISH',
  [Country.VIRGIN_ISLANDS_US]: 'VIRGIN_ISLANDS_US',
  [Country.WALLIS_AND_FUTUNA]: 'WALLIS_AND_FUTUNA',
  [Country.WESTERN_SAHARA]: 'WESTERN_SAHARA',
  [Country.YEMEN]: 'YEMEN',
  [Country.YUGOSLAVIA]: 'YUGOSLAVIA',
  [Country.ZAMBIA]: 'ZAMBIA',
  [Country.ZIMBABWE]: 'ZIMBABWE',
  [Country.COUNT]: '???',
};

export const countryToString = (country: Country): string => {
  return countryToStringMap[country];
};

const regionToStringMap: Record<Region, string> = {
  [Region.NONE]: 'NONE',
  [Region.ALABAMA]: 'ALABAMA',
  [Region.ALASKA]: 'ALASKA',
  [Region.ALBERTA]: 'ALBERTA',
  [Region.ARIZONA]: 'ARIZONA',
  [Region.ARKANSAS]: 'ARKANSAS',
  [Region.AUSTRALIAN_CAPITAL_TERRITORY]: 'AUSTRALIAN_CAPITAL_TERRITORY',
  [Region.BRITISH_COLUMBIA]: 'BRITISH_COLUMBIA',
  [Region.CALIFORNIA]: 'CALIFORNIA',
  [Region.COLORADO]: 'COLORADO',
  [Region.CONNECTICUT]: 'CONNECTICUT',
  [Region.DELAWARE]: 'DELAWARE',
  [Region.DISTRICT_OF_COLUMBIA]: 'DISTRICT_OF_COLUMBIA',
  [Region.FLORIDA]: 'FLORIDA',
  [Region.GEORGIA]: 'GEORGIA',
  [Region.HAWAII]: 'HAWAII',
  [Region.IDAHO]: 'IDAHO',
  [Region.ILLINOIS]: 'ILLINOIS',
  [Region.INDIANA]: 'INDIANA',
  [Region.IOWA]: 'IOWA',
  [Region.KANSAS]: 'KANSAS',
  [Region.KENTUCKY]: 'KENTUCKY',
  [Region.LOUISIANA]: 'LOUISIANA',
  [Region.MAINE]: 'MAINE',
  [Region.MANITOBA]: 'MANITOBA',
  [Region.MARYLAND]: 'MARYLAND',
  [Region.MASSACHUSETTS]: 'MASSACHUSETTS',
  [Region.MICHIGAN]: 'MICHIGAN',
  [Region.MINNESOTA]: 'MINNESOTA',
  [Region.MISSISSIPPI]: 'MISSISSIPPI',
  [Region.MISSOURI]: 'MISSOURI',
  [Region.MONTANA]: 'MONTANA',
  [Region.NEBRASKA]: 'NEBRASKA',
  [Region.NEVADA]: 'NEVADA',
  [Region.NEW_BRUNSWICK]: 'NEW_BRUNSWICK',
  [Region.NEW_HAMPSHIRE]: 'NEW_HAMPSHIRE',
  [Region.NEW_JERSEY]: 'NEW_JERSEY',
  [Region.NEW_MEXICO]: 'NEW_MEXICO',
  [Region.NEW_SOUTH_WALES]: 'NEW_SOUTH_WALES',
  [Region.NEW_YORK]: 'NEW_YORK',
  [Region.NORTHERN_TERRITORY]: 'NORTHERN_TERRITORY',
  [Region.NORTH_CAROLINA]: 'NORTH_CAROLINA',
  [Region.NORTH_DAKOTA]: 'NORTH_DAKOTA',
  [Region.NOVA_SCOTIA]: 'NOVA_SCOTIA',
  [Region.OHIO]: 'OHIO',
  [Region.OKLAHOMA]: 'OKLAHOMA',
  [Region.ONTARIO]: 'ONTARIO',
  [Region.OREGON]: 'OREGON',
  [Region.PENNSYLVANIA]: 'PENNSYLVANIA',
  [Region.QUEBEC]: 'QUEBEC',
  [Region.QUEENSLAND]: 'QUEENSLAND',
  [Region.RHODE_ISLAND]: 'RHODE_ISLAND',
  [Region.SASKATCHEWAN]: 'SASKATCHEWAN',
  [Region.SOUTH_AUSTRALIA]: 'SOUTH_AUSTRALIA',
  [Region.SOUTH_CAROLINA]: 'SOUTH_CAROLINA',
  [Region.SOUTH_DAKOTA]: 'SOUTH_DAKOTA',
  [Region.TASMANIA]: 'TASMANIA',
  [Region.TENNESSEE]: 'TENNESSEE',
  [Region.TEXAS]: 'TEXAS',
  [Region.UTAH]: 'UTAH',
  [Region.VERMONT]: 'VERMONT',
  [Region.VICTORIA]: 'VICTORIA',
  [Region.VIRGINIA]: 'VIRGINIA',
  [Region.WASHINGTON]: 'WASHINGTON',
  [Region.WESTERN_AUSTRALIA]: 'WESTERN_AUSTRALIA',
  [Region.WEST_VIRGINIA]: 'WEST_VIRGINIA',
  [Region.WISCONSIN]: 'WISCONSIN',
  [Region.WYOMING]: 'WYOMING',
  [Region.YUKON]: 'YUKON',
  [Region.CIUDAD_DE_MEXICO]: 'CIUDAD_DE_MEXICO',
  [Region.JALISCO]: 'JALISCO',
  [Region.NEWFOUNDLAND_AND_LABRADOR]: 'NEWFOUNDLAND_AND_LABRADOR',
  [Region.NUEVO_LEON]: 'NUEVO_LEON',
  [Region.BAJA_CALIFORNIA]: 'BAJA_CALIFORNIA',
  [Region.CHIHUAHUA]: 'CHIHUAHUA',
  [Region.GUANAJUATO]: 'GUANAJUATO',
  [Region.GUERRERO]: 'GUERRERO',
  [Region.MEXICO]: 'MEXICO',
  [Region.MICHOACAN]: 'MICHOACAN',
  [Region.NEW_YORK_CITY]: 'NEW_YORK_CITY',
  [Region.TAMAULIPAS]: 'TAMAULIPAS',
  [Region.VERACRUZ]: 'VERACRUZ',
  [Region.CHIAPAS]: 'CHIAPAS',
  [Region.COAHUILA]: 'COAHUILA',
  [Region.DURANGO]: 'DURANGO',
  [Region.GUERRERO_COCULA]: 'GUERRERO_COCULA',
  [Region.GUERRERO_JUCHITAN]: 'GUERRERO_JUCHITAN',
  [Region.GUERRERO_TEPECOACUILCO]: 'GUERRERO_TEPECOACUILCO',
  [Region.GUERRERO_TLACOAPA]: 'GUERRERO_TLACOAPA',
  [Region.GUJARAT]: 'GUJARAT',
  [Region.HIDALGO]: 'HIDALGO',
  [Region.KARNATAKA]: 'KARNATAKA',
  [Region.KERALA]: 'KERALA',
  [Region.KHYBER_PAKHTUNKHWA]: 'KHYBER_PAKHTUNKHWA',
  [Region.MADHYA_PRADESH]: 'MADHYA_PRADESH',
  [Region.MAHARASHTRA]: 'MAHARASHTRA',
  [Region.MORELOS]: 'MORELOS',
  [Region.NAYARIT]: 'NAYARIT',
  [Region.OAXACA]: 'OAXACA',
  [Region.PUEBLA]: 'PUEBLA',
  [Region.PUNJAB]: 'PUNJAB',
  [Region.QUERETARO]: 'QUERETARO',
  [Region.SAN_LUIS_POTOSI]: 'SAN_LUIS_POTOSI',
  [Region.SINALOA]: 'SINALOA',
  [Region.SONORA]: 'SONORA',
  [Region.TABASCO]: 'TABASCO',
  [Region.TAMIL_NADU]: 'TAMIL_NADU',
  [Region.YUCATAN]: 'YUCATAN',
  [Region.ZACATECAS]: 'ZACATECAS',
  [Region.AGUASCALIENTES]: 'AGUASCALIENTES',
  [Region.BAJA_CALIFORNIA_SUR]: 'BAJA_CALIFORNIA_SUR',
  [Region.CAMPECHE]: 'CAMPECHE',
  [Region.COLIMA]: 'COLIMA',
  [Region.QUINTANA_ROO_BENITO_JUAREZ]: 'QUINTANA_ROO_BENITO_JUAREZ',
  [Region.UINTANA_ROO]: 'UINTANA_ROO',
  [Region.QUINTANA_ROO_SOLIDARIDAD]: 'QUINTANA_ROO_SOLIDARIDAD',
  [Region.TLAXCALA]: 'TLAXCALA',
  [Region.QUINTANA_ROO_COZUMEL]: 'QUINTANA_ROO_COZUMEL',
  [Region.SAO_PAOLO]: 'SAO_PAOLO',
  [Region.COUNT]: 'COUNT',
};

export const regionToString = (region: Region): string => {
  return regionToStringMap[region];
};

const documentTypeToStringMap: Record<DocumentType, string> = {
  [DocumentType.NONE]: 'NONE',
  [DocumentType.CONSULAR_ID]: 'CONSULAR_ID',
  [DocumentType.DL]: 'DL',
  [DocumentType.DL_PUBLIC_SERVICES_CARD]: 'DL_PUBLIC_SERVICES_CARD',
  [DocumentType.EMPLOYMENT_PASS]: 'EMPLOYMENT_PASS',
  [DocumentType.FIN_CARD]: 'FIN_CARD',
  [DocumentType.ID]: 'ID',
  [DocumentType.MULTIPURPOSE_ID]: 'MULTIPURPOSE_ID',
  [DocumentType.MyKad]: 'MyKad',
  [DocumentType.MyKid]: 'MyKid',
  [DocumentType.MyPR]: 'MyPR',
  [DocumentType.MyTentera]: 'MyTentera',
  [DocumentType.PAN_CARD]: 'PAN_CARD',
  [DocumentType.PROFESSIONAL_ID]: 'PROFESSIONAL_ID',
  [DocumentType.PUBLIC_SERVICES_CARD]: 'PUBLIC_SERVICES_CARD',
  [DocumentType.RESIDENCE_PERMIT]: 'RESIDENCE_PERMIT',
  [DocumentType.RESIDENT_ID]: 'RESIDENT_ID',
  [DocumentType.TEMPORARY_RESIDENCE_PERMIT]: 'TEMPORARY_RESIDENCE_PERMIT',
  [DocumentType.VOTER_ID]: 'VOTER_ID',
  [DocumentType.WORK_PERMIT]: 'WORK_PERMIT',
  [DocumentType.iKAD]: 'iKAD',
  [DocumentType.MILITARY_ID]: 'MILITARY_ID',
  [DocumentType.MyKAS]: 'MyKAS',
  [DocumentType.SOCIAL_SECURITY_CARD]: 'SOCIAL_SECURITY_CARD',
  [DocumentType.HEALTH_INSURANCE_CARD]: 'HEALTH_INSURANCE_CARD',
  [DocumentType.PASSPORT]: 'PASSPORT',
  [DocumentType.S_PASS]: 'S_PASS',
  [DocumentType.ADDRESS_CARD]: 'ADDRESS_CARD',
  [DocumentType.ALIEN_ID]: 'ALIEN_ID',
  [DocumentType.ALIEN_PASSPORT]: 'ALIEN_PASSPORT',
  [DocumentType.GREEN_CARD]: 'GREEN_CARD',
  [DocumentType.MINORS_ID]: 'MINORS_ID',
  [DocumentType.POSTAL_ID]: 'POSTAL_ID',
  [DocumentType.PROFESSIONAL_DL]: 'PROFESSIONAL_DL',
  [DocumentType.TAX_ID]: 'TAX_ID',
  [DocumentType.WEAPON_PERMIT]: 'WEAPON_PERMIT',
  [DocumentType.VISA]: 'VISA',
  [DocumentType.BORDER_CROSSING_CARD]: 'BORDER_CROSSING_CARD',
  [DocumentType.DRIVER_CARD]: 'DRIVER_CARD',
  [DocumentType.GLOBAL_ENTRY_CARD]: 'GLOBAL_ENTRY_CARD',
  [DocumentType.MyPolis]: 'MyPolis',
  [DocumentType.NEXUS_CARD]: 'NEXUS_CARD',
  [DocumentType.PASSPORT_CARD]: 'PASSPORT_CARD',
  [DocumentType.PROOF_OF_AGE_CARD]: 'PROOF_OF_AGE_CARD',
  [DocumentType.REFUGEE_ID]: 'REFUGEE_ID',
  [DocumentType.TRIBAL_ID]: 'TRIBAL_ID',
  [DocumentType.VETERAN_ID]: 'VETERAN_ID',
  [DocumentType.CITIZENSHIP_CERTIFICATE]: 'CITIZENSHIP_CERTIFICATE',
  [DocumentType.MY_NUMBER_CARD]: 'MY_NUMBER_CARD',
  [DocumentType.CONSULAR_PASSPORT]: 'CONSULAR_PASSPORT',
  [DocumentType.MINORS_PASSPORT]: 'MINORS_PASSPORT',
  [DocumentType.MINORS_PUBLIC_SERVICES_CARD]: 'MINORS_PUBLIC_SERVICES_CARD',
  [DocumentType.COUNT]: 'COUNT',
};
export const documentTypeToString = (documentType: DocumentType): string => {
  return documentTypeToStringMap[documentType];
};

// standard dimension of DL is 85.6mm by 54mm
const STANDARD_DL_ASPECT_RATIO = 85.6 / 54;

const getWidth = (quad: DisplayableQuad) => {
  const leftMost = Math.min(quad.topLeft.x, quad.bottomLeft.x);
  const rightMost = Math.max(quad.topRight.x, quad.bottomRight.x);
  return rightMost - leftMost;
};

/**
 * If a barcode is present, ensures that the barcode lies inside the card quad.
 * If no barcode is present, only checks for card quad.
 */
const isValidDLQuad = (quad: DisplayableQuad, barcodeQuad: DisplayableQuad | null) => {
  if (!quad || quad.detectionStatus === DetectionStatus.Fail) {
    return false;
  }

  const topMost = Math.min(quad.topLeft.y, quad.topRight.y);
  const bottomMost = Math.max(quad.bottomLeft.y, quad.bottomRight.y);
  const leftMost = Math.min(quad.topLeft.x, quad.bottomLeft.x);
  const rightMost = Math.max(quad.topRight.x, quad.bottomRight.x);

  const aspectRatio = (rightMost - leftMost) / (bottomMost - topMost);
  // aspect ratio is within +/- 20% of the standard drivers license aspect ratio
  const isProperAspectRatio =
    aspectRatio > STANDARD_DL_ASPECT_RATIO * 0.8 && aspectRatio < STANDARD_DL_ASPECT_RATIO * 1.2;

  if (!isProperAspectRatio) {
    return false;
  }

  // check that the barcode quad lies within the dl quad (with extra 5% padding on all sides)
  if (barcodeQuad && barcodeQuad.detectionStatus !== DetectionStatus.Fail) {
    const barcodeTopMost = Math.min(barcodeQuad.topLeft.y, barcodeQuad.topRight.y);
    const barcodeBottomMost = Math.max(barcodeQuad.bottomLeft.y, barcodeQuad.bottomRight.y);
    const barcodeLeftMost = Math.min(barcodeQuad.topLeft.x, barcodeQuad.bottomLeft.x);
    const barcodeRightMost = Math.max(barcodeQuad.topRight.x, barcodeQuad.bottomRight.x);

    const barcodeLiesWithinDL =
      barcodeTopMost > topMost * 0.95 &&
      barcodeBottomMost < bottomMost * 1.05 &&
      barcodeLeftMost > leftMost * 0.95 &&
      barcodeRightMost < rightMost * 1.05;

    if (!barcodeLiesWithinDL) {
      return false;
    }
  }

  return true;
};

export const MicroblinkClient = new Client();
